import React, { createContext, useContext, useEffect, useState } from "react";
import { getValue, fetchAndActivate } from "firebase/remote-config";
import { remoteConfig } from "../../firebase";

const RemoteConfigContext = createContext();

const defaultConfig = {
  ebilling_create:
    '{"status":"SCHEDULED_ACTIVE","hours":{"startTime":"07:00","endTime":"22:00"},"timezone":"Asia/Jakarta"}',
  ebilling_payment:
    '{"status":"SCHEDULED_ACTIVE","hours":{"startTime":"07:00","endTime":"22:00"},"timezone":"Asia/Jakarta"}',
  accounting_import_detection: '{"status":"DISABLED"}',
  npwp_registration: '{"status":"DISABLED"}',
  npwp_validation: '{"status":"DISABLED"}',
  npwp_validation_url: "https://s.id/eregcek",
  npwp_registration_url: "https://s.id/eregdaftar",
  npwp_registration_guide_url: "https://s.id/eregtutor",
  // Plain string flag, compared against "enabled" by its consumer — not JSON
  // like the keys above. Stays off until Remote Config says otherwise.
  service_contact_button: "disabled",
};

export const RemoteConfigProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  // Start from the local defaults rather than {}. An empty object made every
  // consumer read undefined, which some gated operations treat as "allowed".
  const [configValues, setConfigValues] = useState(defaultConfig);

  useEffect(() => {
    const initializeRemoteConfig = async () => {
      try {
        setLoading(true);
        // Defaults have to be in place *before* the fetch, otherwise they
        // never apply to the values it activates.
        remoteConfig.defaultConfig = defaultConfig;
        await fetchAndActivate(remoteConfig);
        const ebilling_create =
          getValue(remoteConfig, "ebilling_create").asString() ||
          defaultConfig.ebilling_create;
        const ebilling_payment =
          getValue(remoteConfig, "ebilling_payment").asString() ||
          defaultConfig.ebilling_payment;
        const accounting_import_detection =
          getValue(remoteConfig, "accounting_import_detection").asString() ||
          defaultConfig.accounting_import_detection;
        const npwp_registration =
          getValue(remoteConfig, "npwp_registration").asString() ||
          defaultConfig.npwp_registration;
        const npwp_validation =
          getValue(remoteConfig, "npwp_validation").asString() ||
          defaultConfig.npwp_validation;
        const npwp_validation_url =
          getValue(remoteConfig, "npwp_validation_url").asString() ||
          defaultConfig.npwp_validation_url;
        const npwp_registration_url =
          getValue(remoteConfig, "npwp_registration_url").asString() ||
          defaultConfig.npwp_registration_url;
        const npwp_registration_guide_url =
          getValue(remoteConfig, "npwp_registration_guide_url").asString() ||
          defaultConfig.npwp_registration_guide_url;
        const service_contact_button =
          getValue(remoteConfig, "service_contact_button").asString() ||
          defaultConfig.service_contact_button;
        setConfigValues({
          ebilling_create,
          ebilling_payment,
          accounting_import_detection,
          npwp_registration,
          npwp_validation,
          npwp_validation_url,
          npwp_registration_url,
          npwp_registration_guide_url,
          service_contact_button,
        });
      } catch (error) {
        // Keep the safe local defaults on failure instead of falling back to
        // an empty config that reads as "no restrictions".
        setConfigValues(defaultConfig);
      } finally {
        setLoading(false);
      }
    };

    initializeRemoteConfig();
  }, []);

  return (
    // loading belongs in the context value; as a Provider prop it was simply
    // dropped and consumers could never see it.
    <RemoteConfigContext.Provider value={{ ...configValues, loading }}>
      {children}
    </RemoteConfigContext.Provider>
  );
};

export const useRemoteConfig = () => {
  return useContext(RemoteConfigContext);
};
